import { Component, computed, inject, input, OnInit, signal } from '@angular/core';
import {
  BuildingSectionDetailDto,
  BuildingSectionService,
  BuildingSectionSubtenantAssignmentDto,
  BuildingSectionSubtenantService,
} from '@artemis-software/wr-api';
import { firstValueFrom } from 'rxjs';
import { NbDialogService, NbToastrService } from '@nebular/theme';
import { DeleteDialogComponent } from '@components/dialog/delete-dialog/delete-dialog.component';
import {
  SubtenantAssignDialogComponent,
  SubtenantAssignDialogResult,
} from './subtenant-assign-dialog/subtenant-assign-dialog.component';

interface SectionGroup {
  section: BuildingSectionDetailDto;
  label: string;
  assignments: BuildingSectionSubtenantAssignmentDto[];
}

@Component({
  standalone: false,
  selector: 'wr-building-subtenant-overview',
  templateUrl: './building-subtenant-overview.component.html',
  styleUrl: './building-subtenant-overview.component.scss',
})
export class BuildingSubtenantOverviewComponent implements OnInit {
  readonly buildingId = input.required<string | null>();

  private readonly buildingSectionService = inject(BuildingSectionService);
  private readonly subtenantService = inject(BuildingSectionSubtenantService);
  private readonly dialogService = inject(NbDialogService);
  private readonly toastrService = inject(NbToastrService);

  readonly sections = signal<BuildingSectionDetailDto[]>([]);
  readonly assignments = signal<BuildingSectionSubtenantAssignmentDto[]>([]);
  readonly searchText = signal('');
  readonly loading = signal(true);
  readonly loadError = signal(false);

  readonly groups = computed<SectionGroup[]>(() => {
    const text = this.searchText().trim().toLowerCase();
    const byId = new Map<string, BuildingSectionSubtenantAssignmentDto[]>();
    for (const a of this.assignments()) {
      if (text && !a.tenantName.toLowerCase().includes(text) && !a.sectionLabel.toLowerCase().includes(text)) {
        continue;
      }
      const list = byId.get(a.sectionId) ?? [];
      list.push(a);
      byId.set(a.sectionId, list);
    }
    return this.sections()
      .map(section => ({
        section,
        label: this.sectionLabel(section),
        assignments: (byId.get(section.id) ?? []).sort((a, b) => a.tenantName.localeCompare(b.tenantName)),
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  });

  readonly hasAnyAssignment = computed(() => this.assignments().length > 0);
  readonly hasSearchHits = computed(() => this.groups().some(g => g.assignments.length > 0));

  async ngOnInit() {
    await this.load();
  }

  private async load() {
    const buildingId = this.buildingId();
    if (!buildingId) {
      this.loading.set(false);
      return;
    }
    this.loading.set(true);
    this.loadError.set(false);
    try {
      const [sections, assignments] = await Promise.all([
        firstValueFrom(this.buildingSectionService.findAllByBuildingId(buildingId)),
        firstValueFrom(this.subtenantService.findAllByBuildingId(buildingId)),
      ]);
      this.sections.set(sections);
      this.assignments.set(assignments);
    } catch (e) {
      console.error('Fehler beim Laden der Untermieter', e);
      this.loadError.set(true);
    } finally {
      this.loading.set(false);
    }
  }

  sectionLabel(section: BuildingSectionDetailDto): string {
    const a = section.address;
    return `${a.street} ${a.number}, ${a.zipCode} ${a.city}`;
  }

  async openAssignDialog(presetSectionId?: string) {
    if (this.sections().length === 0) return;
    const result = await firstValueFrom(
      this.dialogService.open(SubtenantAssignDialogComponent, {
        context: {
          sections: this.sections(),
          existingAssignments: this.assignments(),
          presetSectionId: presetSectionId ?? null,
        },
        closeOnBackdropClick: false,
      }).onClose,
    );
    const payload = result as SubtenantAssignDialogResult | undefined;
    if (!payload) return;
    await this.add(payload.sectionId, payload.tenantId);
  }

  private async add(sectionId: string, tenantId: string) {
    try {
      const created = await firstValueFrom(this.subtenantService.addSubtenant(sectionId, tenantId));
      this.assignments.update(list => [...list, created]);
      this.toastrService.success('Untermieter zugewiesen', 'Erfolg');
    } catch (e: any) {
      const msg = e?.status === 409 ? 'Untermieter ist bereits zugewiesen' : 'Fehler beim Zuweisen';
      this.toastrService.danger(msg, 'Fehler');
    }
  }

  async remove(assignment: BuildingSectionSubtenantAssignmentDto) {
    const confirmed = await firstValueFrom(
      this.dialogService.open(DeleteDialogComponent, {
        context: {
          title: 'Untermieter entfernen',
          message: `Möchtest du "${assignment.tenantName}" aus "${assignment.sectionLabel}" wirklich entfernen?`,
        },
      }).onClose,
    );
    if (!confirmed) return;
    try {
      await firstValueFrom(this.subtenantService.removeSubtenant(assignment.sectionId, assignment.tenantId));
      this.assignments.update(list => list.filter(a => a.id !== assignment.id));
      this.toastrService.success('Untermieter entfernt', 'Erfolg');
    } catch {
      this.toastrService.danger('Fehler beim Entfernen', 'Fehler');
    }
  }

  retry() {
    this.load();
  }
}
